<template>
  <div class="ScrollCarousel">
    <component
      :is="tag"
      ref="scrollArea"
      class="ScrollCarousel-track"
      :tabindex="isMobileOrTablet ? 0 : -1"
      :aria-label="ariaLabel"
      @keydown="handleKeydown"
    >
      <slot />
    </component>

    <div v-if="hasIndicator" class="ScrollCarousel-dots">
      <button
        v-for="(item, index) of items"
        :key="item.title"
        type="button"
        class="ScrollCarousel-dot"
        :class="{ 'is-active': index === activePage }"
        :aria-label="item.title"
        :aria-current="index === activePage ? 'true' : undefined"
        @click="scrollToPage(index)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import { clamp } from '@vueuse/core';
import { usePagedScroll, useResponsive } from '@cvo/ui/composables';
import type { ScrollCarouselProps } from './ScrollCarousel.model';

const props = withDefaults(defineProps<ScrollCarouselProps>(), {
  tag: 'ul',
});

const { isMobileOrTablet } = useResponsive();

const scrollArea = useTemplateRef<HTMLElement>('scrollArea');
const { activePage, scrollToPage } = usePagedScroll({ scrollArea });

const hasIndicator = computed(() => props.items.length > 1);

function handleKeydown(event: KeyboardEvent) {
  const lastIndex = props.items.length - 1;
  const target = {
    ArrowLeft: activePage.value - 1,
    ArrowRight: activePage.value + 1,
    Home: 0,
    End: lastIndex,
  }[event.key];

  if (target === undefined) return;

  event.preventDefault();
  scrollToPage(clamp(target, 0, lastIndex));
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .ScrollCarousel {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-3;

    &-track {
      position: relative;
      display: flex;
      justify-content: space-between;
      gap: vars.$dimensions-d-5;
      margin: 0;
      padding: 0;
      list-style: none;
      overflow-x: auto;
      scrollbar-width: none;
      -ms-overflow-style: none;
      // Mandatory, not proximity: the track must always land on a card so it
      // cannot rest between two of them, out of step with the page indicator
      scroll-snap-type: x mandatory;
      // Lets the track bleed to the viewport edges while keeping the page gutter
      margin-inline: calc(-1 * var(--page-padding-x));
      padding-inline: var(--page-padding-x);
      scroll-padding-inline-start: var(--page-padding-x);

      &::-webkit-scrollbar {
        display: none;
      }

      &:focus-visible {
        outline: vars.$dimensions-d-0-25 solid vars.$color-brand-focus;
        outline-offset: vars.$dimensions-d-2;
      }

      @include responsive.desktop {
        overflow-x: visible;
        scroll-snap-type: none;
        margin-inline: vars.$dimensions-d-0;
        padding-inline: vars.$dimensions-d-0;
      }
    }

    &-dots {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: vars.$dimensions-d-2;

      @include responsive.desktop {
        display: none;
      }
    }

    &-dot {
      inline-size: vars.$dimensions-d-2;
      block-size: vars.$dimensions-d-2;
      padding: vars.$dimensions-d-0;
      background-color: vars.$atoms-page-indicator-dot-color-inactive;
      border: none;
      border-radius: vars.$border-radius-full;
      cursor: pointer;
      transition:
        background-color vars.$transition-base ease-in-out,
        inline-size vars.$transition-base ease-in-out;

      &.is-active {
        inline-size: vars.$dimensions-d-4;
        background-color: vars.$atoms-page-indicator-dot-color-active;
      }
    }
  }
}
</style>
